import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AI_URL } from '../api';

const CONVERSATION_KEY = 'dharmadhara-conversation';
const TRIAL_COUNT_KEY = 'dharmadhara-trial-count';
const SIZE_KEY = 'dharmadhara-size';
const ANON_TRIAL_LIMIT = 5;

// Resize bounds (px). Panel is anchored bottom-right and grows toward upper-left.
const MIN_W = 384;   // matches the original w-96
const MIN_H = 360;   // roughly the original usable minimum
const MAX_W = 1150;  // ~3x the default width
const MARGIN = 32;   // keep this much viewport gutter on the left (2rem)
const MOBILE_BP = 640; // Tailwind sm breakpoint

const clampSize = (w, h, vw, vh) => {
    const viewW = vw || (typeof window !== 'undefined' ? window.innerWidth : MIN_W);
    const viewH = vh || (typeof window !== 'undefined' ? window.innerHeight : MIN_H);
    const maxW = Math.min(MAX_W, viewW - MARGIN);
    const maxH = Math.round(viewH * 0.9);
    return {
        w: Math.round(Math.max(MIN_W, Math.min(w, maxW))),
        h: Math.round(Math.max(MIN_H, Math.min(h, maxH))),
    };
};

const defaultSize = () => {
    const vh = typeof window !== 'undefined' ? window.innerHeight : 900;
    return clampSize(MIN_W, Math.round(vh * 0.7));
};

const loadSize = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(SIZE_KEY));
        if (saved && saved.w && saved.h) return clampSize(saved.w, saved.h);
    } catch {
        /* ignore */
    }
    return defaultSize();
};

const QUICK_STARTS = [
    'Where should I begin with Buddhism?',
    'Find me a mantra for compassion',
    'What suttas speak on loving-kindness?',
    'What is the Virtual Temple?',
];

/**
 * Render assistant text, converting markdown links to router links for
 * internal paths (e.g. [Chenrezig](/temple/chenrezig)) and plain anchors
 * for anything external.
 */
const renderMessageText = (text) => {
    const parts = [];
    const linkRegex = /\[([^\]]+)\]\(([^)\s]+)\)/g;
    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = linkRegex.exec(text)) !== null) {
        if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
        const [, label, href] = match;
        if (href.startsWith('/')) {
            parts.push(
                <Link key={key++} to={href} className="text-amber-400 hover:text-amber-300 underline">
                    {label}
                </Link>
            );
        } else {
            parts.push(
                <a key={key++} href={href} target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 underline">
                    {label}
                </a>
            );
        }
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts;
};

const LotusIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.4}>
        <path strokeLinecap="round" strokeLinejoin="round"
            d="M12 21c-1.8-1.2-3-3.3-3-5.4 0-2.4 1.3-4.6 3-6.6 1.7 2 3 4.2 3 6.6 0 2.1-1.2 4.2-3 5.4z" />
        <path strokeLinecap="round" strokeLinejoin="round"
            d="M12 21c-3 .4-6-.7-7.8-2.8 1.4-1 3.2-1.5 5-1.4M12 21c3 .4 6-.7 7.8-2.8-1.4-1-3.2-1.5-5-1.4M5.5 16.5C3.9 15 3 12.9 3.2 10.7c1.9.3 3.7 1.3 4.9 2.8M18.5 16.5c1.6-1.5 2.5-3.6 2.3-5.8-1.9.3-3.7 1.3-4.9 2.8" />
    </svg>
);

const Dharmadhara = () => {
    const { user, isAuthenticated } = useAuth();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState(() => {
        try {
            return JSON.parse(sessionStorage.getItem(CONVERSATION_KEY)) || [];
        } catch {
            return [];
        }
    });
    const [input, setInput] = useState('');
    const [streaming, setStreaming] = useState(false);
    const [toolActivity, setToolActivity] = useState(null);
    const [trialExhausted, setTrialExhausted] = useState(false);
    const [error, setError] = useState(null);
    const [size, setSize] = useState(loadSize);
    const [isMobile, setIsMobile] = useState(
        () => (typeof window !== 'undefined' ? window.innerWidth < MOBILE_BP : false)
    );
    const scrollRef = useRef(null);
    const abortRef = useRef(null);
    const dragRef = useRef(null);
    const sizeRef = useRef(size);

    useEffect(() => {
        sessionStorage.setItem(CONVERSATION_KEY, JSON.stringify(messages));
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, streaming, open, size]);

    useEffect(() => { sizeRef.current = size; }, [size]);

    // Keep the panel inside the viewport when the window is resized.
    useEffect(() => {
        const onResize = () => {
            setIsMobile(window.innerWidth < MOBILE_BP);
            setSize((s) => clampSize(s.w, s.h));
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => () => abortRef.current?.abort(), []);

    useEffect(() => {
        if (isAuthenticated) setTrialExhausted(false);
    }, [isAuthenticated]);

    const trialRemaining = () => {
        const used = Number(localStorage.getItem(TRIAL_COUNT_KEY) || 0);
        return Math.max(0, ANON_TRIAL_LIMIT - used);
    };

    const sendMessage = async (text) => {
        const trimmed = text.trim();
        if (!trimmed || streaming) return;

        if (!isAuthenticated) {
            if (trialRemaining() <= 0) {
                setTrialExhausted(true);
                return;
            }
            localStorage.setItem(TRIAL_COUNT_KEY, String(Number(localStorage.getItem(TRIAL_COUNT_KEY) || 0) + 1));
        }

        setError(null);
        setInput('');
        const history = [...messages, { role: 'user', content: trimmed }];
        setMessages([...history, { role: 'assistant', content: '' }]);
        setStreaming(true);
        setToolActivity(null);

        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const headers = { 'Content-Type': 'application/json' };
            if (user?.idToken) headers.Authorization = `Bearer ${user.idToken}`;

            const response = await fetch(AI_URL, {
                method: 'POST',
                headers,
                signal: controller.signal,
                body: JSON.stringify({ messages: history }),
            });
            if (!response.ok || !response.body) throw new Error(`Request failed (${response.status})`);

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let assistantText = '';

            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();
                for (const line of lines) {
                    if (!line.trim()) continue;
                    let evt;
                    try { evt = JSON.parse(line); } catch { continue; }
                    if (evt.type === 'text') {
                        assistantText += evt.text;
                        setToolActivity(null);
                        setMessages([...history, { role: 'assistant', content: assistantText }]);
                    } else if (evt.type === 'tool') {
                        setToolActivity(evt.name.startsWith('search') ? 'Searching the library…' : 'Consulting the archive…');
                    } else if (evt.type === 'error') {
                        if (evt.statusCode === 429 && !isAuthenticated) setTrialExhausted(true);
                        throw new Error(evt.message || 'Something went wrong');
                    }
                }
            }

            if (!assistantText) {
                setMessages(history);
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('Dharmadhara error:', err);
                setError(err.message || 'Something went wrong. Please try again.');
                setMessages(history);
            }
        } finally {
            setStreaming(false);
            setToolActivity(null);
            abortRef.current = null;
        }
    };

    const clearConversation = () => {
        setMessages([]);
        setError(null);
        sessionStorage.removeItem(CONVERSATION_KEY);
    };

    const persistSize = (s) => {
        try { localStorage.setItem(SIZE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
    };

    // Handle sits in the upper-left corner; the panel is anchored bottom-right,
    // so moving the pointer up-and-left (negative dx/dy) grows width and height.
    const onHandleDown = (e) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        dragRef.current = { x: e.clientX, y: e.clientY, w: sizeRef.current.w, h: sizeRef.current.h };
    };

    const onHandleMove = (e) => {
        if (!dragRef.current) return;
        const dx = e.clientX - dragRef.current.x;
        const dy = e.clientY - dragRef.current.y;
        const nextW = isMobile ? dragRef.current.w : dragRef.current.w - dx;
        const nextH = dragRef.current.h - dy;
        setSize(clampSize(nextW, nextH));
    };

    const onHandleUp = (e) => {
        if (!dragRef.current) return;
        dragRef.current = null;
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        persistSize(sizeRef.current);
    };

    // Keyboard resize for accessibility (arrows nudge by 24px).
    const onHandleKey = (e) => {
        const STEP = 24;
        let { w, h } = sizeRef.current;
        if (e.key === 'ArrowLeft') w += STEP;
        else if (e.key === 'ArrowRight') w -= STEP;
        else if (e.key === 'ArrowUp') h += STEP;
        else if (e.key === 'ArrowDown') h -= STEP;
        else return;
        e.preventDefault();
        const next = clampSize(isMobile ? sizeRef.current.w : w, h);
        setSize(next);
        persistSize(next);
    };

    // Text and bubble width scale with the panel width.
    const effectiveW = isMobile ? MIN_W : size.w;
    const textClass = effectiveW >= 760 ? 'text-lg' : effectiveW >= 520 ? 'text-base' : 'text-sm';
    const bubbleMax = effectiveW >= 760 ? 'max-w-[75%]' : effectiveW >= 520 ? 'max-w-[80%]' : 'max-w-[85%]';
    const chipClass = effectiveW >= 760 ? 'text-sm' : 'text-xs';
    const panelStyle = isMobile ? { height: size.h } : { width: size.w, height: size.h };

    if (!open) {
        return (
            <button
                onClick={() => setOpen(true)}
                className="fixed bottom-20 right-6 z-50 w-11 h-11 rounded-full bg-slate-800/80 backdrop-blur-sm border border-amber-500/30 text-amber-300 flex items-center justify-center hover:bg-slate-700/90 hover:border-amber-400/60 transition-all shadow-lg"
                title="Ask Dharmadhara"
                aria-label="Open Dharmadhara, the library guide"
            >
                <LotusIcon className="w-6 h-6" />
            </button>
        );
    }

    return (
        <div
            style={panelStyle}
            className="fixed bottom-20 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] flex flex-col bg-slate-900/95 backdrop-blur-md border border-amber-500/30 rounded-xl shadow-2xl overflow-hidden"
        >
            {/* Resize handle (upper-left corner) */}
            <div
                role="button"
                tabIndex={0}
                aria-label="Resize chat window"
                title="Drag to resize"
                onPointerDown={onHandleDown}
                onPointerMove={onHandleMove}
                onPointerUp={onHandleUp}
                onPointerCancel={onHandleUp}
                onKeyDown={onHandleKey}
                style={{ touchAction: 'none' }}
                className={`absolute top-0 left-0 z-20 p-1.5 text-slate-500 hover:text-amber-300 focus:text-amber-300 focus:outline-none select-none ${isMobile ? 'cursor-ns-resize' : 'cursor-nwse-resize'}`}
            >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
                    <path d="M2 5 L5 2 M2 8 L8 2 M2 11 L11 2" />
                </svg>
            </div>
            {/* Header */}
            <div className="flex items-start justify-between pl-7 pr-4 py-3 border-b border-slate-700 bg-slate-900">
                <div className="flex items-center gap-2">
                    <LotusIcon className="w-6 h-6 text-amber-300 flex-shrink-0" />
                    <div>
                        <div className="text-white font-semibold leading-tight">Dharmadhara</div>
                        <div className="text-gray-400 text-[11px] leading-snug">
                            Preserving, clarifying, and sharing the Dharma with wisdom and compassion
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1 ml-2">
                    {messages.length > 0 && (
                        <button
                            onClick={clearConversation}
                            className="text-gray-500 hover:text-gray-300 p-1"
                            title="Clear conversation"
                            aria-label="Clear conversation"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                            </svg>
                        </button>
                    )}
                    <button
                        onClick={() => setOpen(false)}
                        className="text-gray-500 hover:text-gray-300 p-1"
                        title="Close"
                        aria-label="Close Dharmadhara"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[10rem]">
                {messages.length === 0 && (
                    <div className="space-y-3">
                        <p className={`text-gray-300 ${textClass}`}>
                            I help you find teachings in our library — and point you toward the right teachers.
                            How may I assist?
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {QUICK_STARTS.map(q => (
                                <button
                                    key={q}
                                    onClick={() => sendMessage(q)}
                                    className={`${chipClass} text-amber-300/90 border border-amber-500/30 rounded-full px-3 py-1 hover:bg-slate-800 hover:border-amber-400/60 transition-all text-left`}
                                >
                                    {q}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {messages.map((m, i) => (
                    <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                        <div className={`${bubbleMax} ${textClass} rounded-lg px-3 py-2 whitespace-pre-wrap leading-relaxed ${
                            m.role === 'user'
                                ? 'bg-amber-600/20 text-amber-50 border border-amber-500/20'
                                : 'bg-slate-800/80 text-gray-200 border border-slate-700'
                        }`}>
                            {m.role === 'assistant' ? renderMessageText(m.content) : m.content}
                            {m.role === 'assistant' && streaming && i === messages.length - 1 && (
                                <span className="inline-block w-1.5 h-4 ml-0.5 bg-amber-400/80 animate-pulse align-text-bottom" />
                            )}
                        </div>
                    </div>
                ))}
                {toolActivity && (
                    <div className="text-gray-500 text-xs italic">{toolActivity}</div>
                )}
                {error && (
                    <div className="text-red-400/90 text-xs">{error}</div>
                )}
            </div>

            {/* Trial-exhausted gate or input */}
            {trialExhausted && !isAuthenticated ? (
                <div className="px-4 py-4 border-t border-slate-700 bg-slate-900 text-center">
                    <p className={`text-gray-300 ${textClass} mb-3`}>
                        You've reached the visitor limit. Create a free account to continue our conversation —
                        and receive guidance aware of your tradition and practice.
                    </p>
                    <Link
                        to="/auth"
                        className="inline-block bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-lg px-5 py-2 transition-colors no-underline"
                    >
                        Sign in / Create account
                    </Link>
                </div>
            ) : (
                <form
                    onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
                    className="flex items-end gap-2 px-3 py-3 border-t border-slate-700 bg-slate-900"
                >
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage(input);
                            }
                        }}
                        placeholder="Ask about mantras, sutras, practices…"
                        rows={1}
                        className={`flex-1 resize-none bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 ${textClass} text-gray-200 placeholder-gray-500 focus:outline-none focus:border-amber-500/60`}
                        disabled={streaming}
                    />
                    <button
                        type="submit"
                        disabled={streaming || !input.trim()}
                        className="bg-amber-600 hover:bg-amber-500 disabled:bg-slate-700 disabled:text-gray-500 text-white rounded-lg p-2 transition-colors flex-shrink-0"
                        aria-label="Send message"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                        </svg>
                    </button>
                </form>
            )}

            {!isAuthenticated && !trialExhausted && messages.length > 0 && (
                <div className="px-4 pb-2 bg-slate-900 text-center">
                    <span className="text-gray-600 text-[10px]">
                        {trialRemaining()} visitor message{trialRemaining() === 1 ? '' : 's'} remaining · <Link to="/auth" className="text-gray-500 hover:text-amber-400 underline">sign in</Link> for more
                    </span>
                </div>
            )}
        </div>
    );
};

export default Dharmadhara;
